<template>
  <section :class="[$style.memo, { [$style['memo--editing']]: isEditing }]">
    <header :class="$style['memo__header']">
      <p>
        {{ title }}
      </p>
      <div :class="$style.memo__action">
        <button
          v-if="isEditing"
          :class="{
            [$style.button]: true,
            [$style['button--edit']]: true,
            [$style['button--disabled']]: disabled,
          }"
          @click="saveMemo"
          :disabled="disabled"
        >
          保存
        </button>
        <button
          :class="{
            [$style.button]: true,
            [$style['button--delete']]: true,
            [$style['button--disabled']]: disabled,
          }"
          @click="openModal"
          :disabled="disabled"
        >
          削除
        </button>
        <ControlledModal>
          <ConfirmationModal
            title="メモを削除"
            buttonText="メモを削除"
            :disabled="disabled"
            @confirm="deleteMemo"
            @close="closeModal"
          >
            <p>メモを削除します、よろしいですか？</p>
          </ConfirmationModal>
        </ControlledModal>
      </div>
    </header>
    <DolphinTextarea
      v-if="isEditing"
      v-model="content"
      auto-grow
      :class="[$style['memo__content'], $style['memo__content--edit']]"
    />
    <ChatRoomMemoContent v-else :text="content" />
  </section>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import { useModal } from '@/hooks/useModal'
import ChatRoomMemoContent from './ChatRoomMemoContent.vue'

const props = withDefaults(
  defineProps<{
    title: string
    content: string
    isEditing?: boolean
    disabled: boolean
  }>(),
  {
    isEditing: false,
  }
)

const emit = defineEmits<{
  (event: 'save', content: string): void
  (event: 'delete'): void
}>()

const content = ref(props.content)

const { ControlledModal, openModal, closeModal } = useModal()

const saveMemo = () => {
  emit('save', content.value)
}

const deleteMemo = () => {
  emit('delete')
  closeModal()
}
</script>

<style lang="scss" module>
.button {
  &--edit {
    color: $color-text-and-object-accent-primary;
  }

  &--delete {
    color: $color-caution-accent;
  }

  &--disabled {
    cursor: not-allowed;
    color: $color-text-and-object-disable;
  }
}

.memo {
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;
  padding: 10px;
  position: relative;

  &--editing {
    border: none;
    padding: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    top: -11px;
    left: 0;
    padding: 0 8px;
    color: $color-text-and-object-low-emphasis;
    font-size: 12px;
    width: 100%;

    & > * {
      background-color: $color-label-white;
      padding: 0 5px;
    }
  }

  &__action {
    font-size: 13px;

    & > *:nth-child(n + 2) {
      margin-left: 15px;
    }
  }

  &__content {
    padding: 10px 0 0 10px;
    color: $color-text-and-object-high-emphasis;
    white-space: pre-wrap;
    word-break: break-all;

    &--edit {
      font-size: 14px;
      padding: 20px 0 0 20px;
    }
  }
}
</style>
